/**
 * ReorderControls — the move affordances every builder slot carries
 * (QuestionCard and the locked ContactCaptureCard).
 *
 * Why this exists: HTML5 drag reorders one slot at a time and does NOT scroll
 * the page, so on a 40-question form moving the last question to the top meant
 * lift → drag → drop → scroll → repeat, a dozen times over. These controls make
 * the long moves one click:
 *   - ↑ / ↓ on EVERY viewport (they used to be mobile-only, `sm:hidden`);
 *   - "Move to…" → Top / Bottom, or a position number to jump straight there.
 * Drag still works, and FormBuilder now auto-scrolls the window while dragging.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, MoveVertical } from 'lucide-react';
import { Z } from '../ui';

interface ReorderControlsProps {
  /** 1-based position of this slot in the list. */
  position: number;
  /** Total slots in the list. */
  total: number;
  /** Move this slot to a 1-based position (clamped by the caller). */
  onMoveTo: (position: number) => void;
  /** Label used in aria-labels ("question", "contact capture"). */
  what?: string;
}

const menuBtn =
  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent dark:text-gray-200 dark:hover:bg-gray-700';

export default function ReorderControls({ position, total, onMoveTo, what = 'question' }: ReorderControlsProps) {
  const [open, setOpen] = useState(false);
  const [jump, setJump] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const canUp = position > 1;
  const canDown = position < total;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const move = (to: number) => {
    setOpen(false);
    setJump('');
    const clamped = Math.min(Math.max(to, 1), total);
    if (clamped !== position) onMoveTo(clamped);
  };

  const submitJump = () => {
    const n = Number(jump);
    if (!Number.isFinite(n) || n < 1) { setOpen(false); setJump(''); return; }
    move(Math.round(n));
  };

  return (
    <div ref={wrapRef} className="relative flex shrink-0 flex-col items-center">
      <button
        type="button"
        onClick={() => move(position - 1)}
        disabled={!canUp}
        className="rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700 dark:hover:text-gray-200"
        aria-label={`Move ${what} up`}
        title="Move up"
      >
        <ArrowUp className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        aria-label={`Move ${what} to a position`}
        aria-expanded={open}
        title="Move to…"
      >
        <MoveVertical className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => move(position + 1)}
        disabled={!canDown}
        className="rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700 dark:hover:text-gray-200"
        aria-label={`Move ${what} down`}
        title="Move down"
      >
        <ArrowDown className="h-4 w-4" />
      </button>

      {open && (
        <div className={`absolute right-0 top-full ${Z.menu} mt-1 w-52 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg dark:border-gray-700 dark:bg-gray-800`}>
          <button type="button" className={menuBtn} onClick={() => move(1)} disabled={!canUp}>
            <ChevronsUp className="h-4 w-4" /> Move to top
          </button>
          <button type="button" className={menuBtn} onClick={() => move(total)} disabled={!canDown}>
            <ChevronsDown className="h-4 w-4" /> Move to bottom
          </button>
          <div className="mt-1 border-t border-gray-200 pt-1.5 dark:border-gray-700">
            <label className="mb-1 block px-2 text-xs text-gray-500 dark:text-gray-400">
              Move to position (1–{total})
            </label>
            <div className="flex items-center gap-1.5 px-2 pb-1">
              <input
                type="number"
                min={1}
                max={total}
                value={jump}
                autoFocus
                onChange={(e) => setJump(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); submitJump(); }
                }}
                placeholder={String(position)}
                className="w-20 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
              <button
                type="button"
                onClick={submitJump}
                className="rounded bg-primary-600 px-2.5 py-1 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700"
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
